<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-alert -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-alert */
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const route = useRoute();
const { currentAccount } = useAccount();
const inboxMenuOpen = ref(false);
const methodMenuOpen = ref(false);
const eventMenuOpen = ref(false);
const templateMenuOpen = ref(false);
const rules = ref([]);
const inboxes = ref([]);
const customTemplates = ref([]);
const automationEnabled = ref(false);
const saving = ref(false);
const error = ref('');
const testResult = ref('');
const testResponse = ref(null);
const testing = ref(false);
const editingRuleId = ref(null);
const literalBraces = { open: '{', close: '}' };
const bodyPlaceholder = computed(() =>
  t('BILLING_AUTOMATION.BODY_PLACEHOLDER', literalBraces)
);
const webhookHeaders = ref([]);
const templateVariableValues = reactive({
  nome: 'Bruno',
  valor: 'R$ 147,68',
  vencimento: '20/08/2026',
  link: 'https://exemplo.com/boleto',
  pix: '000201010212...',
  1: 'Bruno',
  2: 'R$ 147,68',
  3: '20/08/2026',
});
const form = reactive({
  name: '',
  inbox_id: '',
  source_type: 'webhook',
  integration_provider: 'sgp',
  active: false,
  event_type: 'before_due',
  days: 3,
  execution_hour: 2,
  max_deliveries_per_minute: 120,
  webhook_url: '',
  webhook_method: 'POST',
  webhook_headers: {},
  webhook_body: {},
  response_mapping: {
    invoices_path: 'invoices',
    invoice_id_path: 'id',
    due_date_path: 'due_date',
    amount_path: 'amount',
    link_path: 'link',
    pix_path: 'pix',
  },
  webhook_headers_json: '{}',
  webhook_body_json: '{}',
  template_name: '',
  template_language: 'pt_BR',
  message_template: t('BILLING_AUTOMATION.DEFAULT_MESSAGE', literalBraces),
});

const selectedInbox = computed(() =>
  inboxes.value.find(inbox => String(inbox.id) === String(form.inbox_id))
);
const selectedInboxLabel = computed(
  () => selectedInbox.value?.name || t('BILLING_AUTOMATION.SELECT_INBOX')
);
const eventLabel = computed(() => {
  const labels = {
    before_due: t('BILLING_AUTOMATION.BEFORE_DUE'),
    on_due: t('BILLING_AUTOMATION.ON_DUE'),
    after_due: t('BILLING_AUTOMATION.AFTER_DUE'),
  };

  return labels[form.event_type] || t('BILLING_AUTOMATION.BEFORE_DUE');
});
const eventLabelFor = eventType =>
  ({
    before_due: t('BILLING_AUTOMATION.BEFORE_DUE'),
    on_due: t('BILLING_AUTOMATION.ON_DUE'),
    after_due: t('BILLING_AUTOMATION.AFTER_DUE'),
  })[eventType] || eventType;
const nativeIntegrationEnabled = computed(() => {
  const modules = currentAccount.value?.enabled_modules || {};
  return modules.sgp_integration === true || modules.sgp_integration === 'true';
});
const nativeSourceClass = computed(() => {
  if (!nativeIntegrationEnabled.value) {
    return 'cursor-not-allowed border-n-slate-5 bg-n-slate-2 opacity-60';
  }

  return form.source_type === 'native'
    ? 'border-n-brand bg-n-brand/10'
    : 'border-n-slate-5 bg-n-slate-2 hover:border-n-slate-6';
});
const nativeSourceDescription = computed(() =>
  nativeIntegrationEnabled.value
    ? t('BILLING_AUTOMATION.SGP_CONNECTED')
    : t('BILLING_AUTOMATION.SGP_UNAVAILABLE')
);
const selectInbox = inboxId => {
  form.inbox_id = inboxId;
  form.template_name = '';
  inboxMenuOpen.value = false;
};
const selectMethod = method => {
  form.webhook_method = method;
  methodMenuOpen.value = false;
};
const selectEvent = eventType => {
  form.event_type = eventType;
  if (eventType === 'on_due') form.days = 0;
  eventMenuOpen.value = false;
};
const clampDays = () => {
  const value = Number.parseInt(form.days, 10);
  form.days = Number.isNaN(value) ? 0 : Math.min(30, Math.max(0, value));
};
const selectTemplate = templateName => {
  form.template_name = templateName;
  templateMenuOpen.value = false;
};
const whatsappInboxes = computed(() =>
  inboxes.value.filter(inbox => {
    const channelType = String(inbox.channel_type).toLowerCase();
    const integration = String(
      inbox.additional_attributes?.integration || ''
    ).toLowerCase();
    const testOnly = inbox.additional_attributes?.test_only === true;

    return (
      channelType.includes('whatsapp') ||
      (channelType === 'channel::api' &&
        (integration === 'evolution' ||
          (integration === 'billing_simulation' && testOnly)))
    );
  })
);
const approvedTemplates = computed(() => {
  const inboxTemplates =
    selectedInbox.value?.message_templates ||
    selectedInbox.value?.additional_attributes?.message_templates ||
    [];
  const linkedCustomTemplates = customTemplates.value.filter(
    template => String(template.inbox_id) === String(form.inbox_id)
  );
  const templates = [...inboxTemplates, ...linkedCustomTemplates];

  return Array.isArray(templates)
    ? templates.filter(
        (template, index, allTemplates) =>
          String(template.status).toLowerCase() === 'approved' &&
          allTemplates.findIndex(item => item.name === template.name) === index
      )
    : [];
});
const selectedTemplate = computed(() =>
  approvedTemplates.value.find(template => template.name === form.template_name)
);
const selectedTemplateBody = computed(() => {
  if (selectedTemplate.value?.content) return selectedTemplate.value.content;
  const body = selectedTemplate.value?.components?.find(
    component => String(component.type).toLowerCase() === 'body'
  );

  return (
    body?.text || t('BILLING_AUTOMATION.DEFAULT_TEMPLATE_BODY', literalBraces)
  );
});
const selectedTemplateHeader = computed(() => {
  const header = selectedTemplate.value?.components?.find(
    component => String(component.type).toLowerCase() === 'header'
  );

  return (
    header?.text ||
    selectedTemplate.value?.name ||
    t('BILLING_AUTOMATION.COMPANY')
  );
});
const selectedTemplateImage = computed(() => {
  const variables = selectedTemplate.value?.variables || {};
  const header = selectedTemplate.value?.components?.find(
    component => String(component.type).toLowerCase() === 'header'
  );

  return variables.headerUrl || header?.example?.header_handle?.[0] || '';
});
const selectedTemplatePayment = computed(() => {
  const variables = selectedTemplate.value?.variables || {};
  return (
    variables.payment_details === true || variables.payment_details === 'true'
  );
});
const selectedTemplateButtons = computed(() => {
  if (Array.isArray(selectedTemplate.value?.buttons)) {
    return selectedTemplate.value.buttons;
  }

  return (
    selectedTemplate.value?.components?.find(
      component => String(component.type).toLowerCase() === 'buttons'
    )?.buttons || []
  );
});
const templateVariableNames = computed(() => {
  const names = [
    ...selectedTemplateBody.value.matchAll(/\{\{([^}]+)\}\}/g),
  ].map(match => match[1].trim());

  return [...new Set(names)];
});
const selectedTemplatePreview = computed(() =>
  selectedTemplateBody.value.replace(
    /\{\{([^}]+)\}\}/g,
    (_match, key) => templateVariableValues[key.trim()] || `{{${key}}}`
  )
);
const availableVariables = computed(() => {
  const normalized =
    form.source_type === 'native'
      ? [
          {
            token: '{{nome}}',
            source: t('BILLING_AUTOMATION.VAR_CUSTOMER_NAME'),
          },
          {
            token: '{{valor}}',
            source: t('BILLING_AUTOMATION.VAR_INVOICE_AMOUNT'),
          },
          {
            token: '{{vencimento}}',
            source: t('BILLING_AUTOMATION.VAR_DUE_DATE'),
          },
          {
            token: '{{link}}',
            source: t('BILLING_AUTOMATION.VAR_INVOICE_LINK'),
          },
          { token: '{{pix}}', source: t('BILLING_AUTOMATION.VAR_PIX_CODE') },
        ]
      : [];
  const rawResponse = testResponse.value;
  const firstInvoice = Array.isArray(rawResponse)
    ? rawResponse[0]
    : rawResponse?.invoices?.[0] || rawResponse?.faturas?.[0];
  const rawFields =
    firstInvoice && typeof firstInvoice === 'object'
      ? Object.keys(firstInvoice).map(key => ({
          token: `{{${key}}}`,
          source: t('BILLING_AUTOMATION.VAR_WEBHOOK_RESPONSE'),
        }))
      : [];

  return [
    ...normalized,
    ...rawFields.filter(
      item => !normalized.some(base => base.token === item.token)
    ),
  ];
});

const endpoint = () =>
  `/api/v1/accounts/${route.params.accountId}/billing_automation_rules`;

const friendlyError = errorMessage => {
  const messages = {
    'Inbox must exist': t('BILLING_AUTOMATION.ERROR_INBOX_REQUIRED'),
    "Name can't be blank": t('BILLING_AUTOMATION.ERROR_NAME_REQUIRED'),
    "Webhook url can't be blank": t('BILLING_AUTOMATION.ERROR_URL_REQUIRED'),
    "Template name can't be blank": t(
      'BILLING_AUTOMATION.ERROR_TEMPLATE_REQUIRED'
    ),
  };

  return String(errorMessage || '')
    .split(', ')
    .map(message => messages[message] || message)
    .join(' ');
};

const load = async () => {
  const [rulesResponse, inboxResponse, templatesResponse] = await Promise.all([
    window.axios.get(endpoint()),
    window.axios.get(`/api/v1/accounts/${route.params.accountId}/inboxes`),
    window.axios.get(
      `/api/v1/accounts/${route.params.accountId}/custom_message_templates`
    ),
  ]);
  rules.value = rulesResponse.data.rules || [];
  automationEnabled.value = rulesResponse.data.automation_enabled === true;
  inboxes.value = inboxResponse.data.payload || inboxResponse.data;
  customTemplates.value = templatesResponse.data || [];
};

const addHeader = () => {
  webhookHeaders.value.push({ key: '', value: '' });
};

const removeHeader = index => {
  webhookHeaders.value.splice(index, 1);
};

const buildPayload = () => ({
  ...form,
  webhook_headers: webhookHeaders.value.reduce((headers, header) => {
    if (header.key.trim()) headers[header.key.trim()] = header.value;
    return headers;
  }, {}),
  webhook_body: JSON.parse(form.webhook_body_json || '{}'),
});

const testRequest = async () => {
  error.value = '';
  testResult.value = '';
  testResponse.value = null;
  testing.value = true;
  try {
    const response = await window.axios.post(`${endpoint()}/test`, {
      billing_automation_rule: buildPayload(),
    });
    if (!response.data.success) {
      error.value = response.data.error || t('BILLING_AUTOMATION.ERROR_TEST');
      return;
    }
    testResponse.value = response.data.response;
    testResult.value = JSON.stringify(response.data.response, null, 2);
  } catch (e) {
    error.value =
      e instanceof SyntaxError
        ? t('BILLING_AUTOMATION.ERROR_BODY_JSON')
        : friendlyError(e.response?.data?.error) ||
          t('BILLING_AUTOMATION.ERROR_TEST');
  } finally {
    testing.value = false;
  }
};

const save = async () => {
  error.value = '';
  saving.value = true;
  try {
    const payload = buildPayload();
    const response = editingRuleId.value
      ? await window.axios.patch(`${endpoint()}/${editingRuleId.value}`, {
          billing_automation_rule: payload,
        })
      : await window.axios.post(endpoint(), {
          billing_automation_rule: payload,
        });
    if (editingRuleId.value) {
      const index = rules.value.findIndex(
        rule => rule.id === editingRuleId.value
      );
      if (index >= 0) rules.value[index] = response.data;
    } else {
      rules.value.push(response.data);
    }
    editingRuleId.value = null;
    form.name = '';
    form.inbox_id = '';
    form.template_name = '';
    form.webhook_url = '';
  } catch (e) {
    error.value =
      e instanceof SyntaxError
        ? t('BILLING_AUTOMATION.ERROR_WEBHOOK_JSON')
        : friendlyError(e.response?.data?.error) ||
          t('BILLING_AUTOMATION.ERROR_SAVE');
  } finally {
    saving.value = false;
  }
};

const editRule = rule => {
  editingRuleId.value = rule.id;
  Object.assign(form, {
    name: rule.name || '',
    inbox_id: rule.inbox_id || '',
    source_type: rule.source_type || 'webhook',
    integration_provider: rule.integration_provider || 'sgp',
    active: rule.active === true,
    event_type: rule.event_type || 'before_due',
    days: rule.days ?? 3,
    execution_hour: Number.isInteger(rule.execution_hour)
      ? rule.execution_hour
      : 2,
    max_deliveries_per_minute: rule.max_deliveries_per_minute || 120,
    webhook_url: rule.webhook_url || '',
    webhook_method: rule.webhook_method || 'POST',
    webhook_headers: rule.webhook_headers || {},
    webhook_body: rule.webhook_body || {},
    response_mapping: rule.response_mapping || form.response_mapping,
    webhook_headers_json: JSON.stringify(rule.webhook_headers || {}, null, 2),
    webhook_body_json: JSON.stringify(rule.webhook_body || {}, null, 2),
    template_name: rule.template_name || '',
    template_language: rule.template_language || 'pt_BR',
    message_template: rule.message_template || '',
  });
  webhookHeaders.value = Object.entries(rule.webhook_headers || {}).map(
    ([key, value]) => ({ key, value })
  );
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const cancelEdit = () => {
  editingRuleId.value = null;
  form.name = '';
  form.inbox_id = '';
  form.template_name = '';
  form.webhook_url = '';
  webhookHeaders.value = [];
};

const toggle = async rule => {
  const response = await window.axios.patch(`${endpoint()}/${rule.id}`, {
    billing_automation_rule: { active: !rule.active },
  });
  Object.assign(rule, response.data);
};

const toggleAutomation = async () => {
  const response = await window.axios.post(`${endpoint()}/toggle`, {
    enabled: !automationEnabled.value,
  });
  automationEnabled.value = response.data.automation_enabled;
};

const remove = async rule => {
  if (
    !window.confirm(t('BILLING_AUTOMATION.CONFIRM_REMOVE', { name: rule.name }))
  )
    return;
  await window.axios.delete(`${endpoint()}/${rule.id}`);
  rules.value = rules.value.filter(item => item.id !== rule.id);
};

onMounted(load);
</script>

<template>
  <div class="mx-auto flex max-w-6xl flex-col gap-6 p-6">
    <section
      class="relative overflow-hidden rounded-2xl border border-n-slate-5 bg-n-slate-2 p-6 shadow-sm"
    >
      <div
        class="absolute -right-16 -top-20 size-56 rounded-full bg-n-brand/10 blur-3xl"
      />
      <div class="relative flex items-start justify-between gap-6">
        <div class="flex items-start gap-4">
          <div
            class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-n-brand/15 text-n-brand"
          >
            <span class="i-lucide-receipt-text size-6" />
          </div>
          <div>
            <div class="mb-1 flex items-center gap-2">
              <h1 class="text-2xl font-semibold tracking-tight text-n-slate-12">
                {{ $t('BILLING_AUTOMATION.TITLE') }}
              </h1>
              <span
                class="rounded-full bg-n-brand/15 px-2.5 py-1 text-xs font-medium text-n-brand"
                >{{ $t('BILLING_AUTOMATION.AUTOMATION') }}</span
              >
            </div>
            <p class="max-w-2xl text-sm leading-6 text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="relative hidden items-center gap-3 rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-left text-xs text-n-slate-11 transition hover:border-n-brand sm:flex"
          :aria-pressed="automationEnabled"
          :aria-label="
            automationEnabled
              ? $t('BILLING_AUTOMATION.DISABLE_AUTOMATION')
              : $t('BILLING_AUTOMATION.ENABLE_AUTOMATION')
          "
          @click="toggleAutomation"
        >
          <span
            class="relative inline-flex h-6 w-11 shrink-0 rounded-full p-0.5 transition-colors"
            :class="automationEnabled ? 'bg-n-brand' : 'bg-n-slate-6'"
          />
          <span
            class="pointer-events-none absolute ml-0.5 size-5 rounded-full bg-white shadow transition-transform"
            :class="automationEnabled ? 'translate-x-5' : 'translate-x-0'"
          />
          <span>
            <strong class="block font-medium text-n-slate-12">{{
              $t('BILLING_AUTOMATION.AUTO_EXECUTION')
            }}</strong>
            <span>{{
              automationEnabled
                ? $t('BILLING_AUTOMATION.AUTO_ACTIVE')
                : $t('BILLING_AUTOMATION.AUTO_INACTIVE')
            }}</span>
          </span>
        </button>
      </div>
    </section>

    <section
      class="rounded-2xl border border-n-slate-5 bg-n-slate-2 p-6 shadow-sm"
    >
      <div
        class="mb-6 flex items-center justify-between gap-4 border-b border-n-slate-5 pb-5"
      >
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{
              editingRuleId
                ? $t('BILLING_AUTOMATION.EDIT_RULE')
                : $t('BILLING_AUTOMATION.CREATE_RULE')
            }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('BILLING_AUTOMATION.FORM_DESCRIPTION') }}
          </p>
        </div>
        <span
          class="hidden rounded-lg bg-n-slate-1 px-3 py-2 text-xs text-n-slate-11 sm:block"
          >{{ $t('BILLING_AUTOMATION.STEP', { current: 1, total: 3 }) }}</span
        >
      </div>

      <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label class="block text-sm font-medium text-n-slate-12">
          {{ $t('BILLING_AUTOMATION.NAME') }}
          <input
            v-model="form.name"
            class="mt-2 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2.5 outline-none transition focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            :placeholder="$t('BILLING_AUTOMATION.NAME_PLACEHOLDER')"
          />
        </label>
        <label class="block text-sm font-medium text-n-slate-12">
          {{ $t('BILLING_AUTOMATION.INBOX') }}
          <div class="relative mt-2">
            <button
              type="button"
              class="flex w-full items-center justify-between rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2.5 text-left outline-none transition focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
              @click="inboxMenuOpen = !inboxMenuOpen"
            >
              <span>{{ selectedInboxLabel }}</span>
              <i
                class="i-lucide-chevron-down ml-3 shrink-0 text-base text-n-slate-10"
                aria-hidden="true"
              />
            </button>
            <div
              v-if="inboxMenuOpen"
              class="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-n-slate-5 bg-n-slate-1 shadow-xl"
            >
              <button
                type="button"
                class="block w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-slate-2"
                @click="selectInbox('')"
              >
                {{ $t('BILLING_AUTOMATION.SELECT_INBOX') }}
              </button>
              <button
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                type="button"
                class="block w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-slate-2"
                @click="selectInbox(inbox.id)"
              >
                {{ inbox.name }}
              </button>
            </div>
          </div>
        </label>
      </div>

      <div class="mb-6 rounded-xl border border-n-slate-5 bg-n-slate-1 p-4">
        <div class="mb-4 flex items-center gap-3">
          <span
            class="flex size-8 items-center justify-center rounded-lg bg-n-brand/15 text-sm font-semibold text-n-brand"
            >01</span
          >
          <div>
            <h3 class="font-medium text-n-slate-12">
              {{ $t('BILLING_AUTOMATION.DATA_SOURCE') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.DATA_SOURCE_DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <button
            type="button"
            class="flex items-center gap-3 rounded-xl border p-4 text-left transition"
            :class="
              form.source_type === 'webhook'
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-slate-5 bg-n-slate-2 hover:border-n-slate-6'
            "
            @click="form.source_type = 'webhook'"
          >
            <span class="i-lucide-webhook size-5 text-n-brand" /><span
              ><strong class="block text-sm text-n-slate-12">{{
                $t('BILLING_AUTOMATION.CUSTOM_WEBHOOK')
              }}</strong
              ><small class="text-xs text-n-slate-11">{{
                $t('BILLING_AUTOMATION.CUSTOM_WEBHOOK_DESCRIPTION')
              }}</small></span
            >
          </button>
          <button
            type="button"
            class="flex items-center gap-3 rounded-xl border p-4 text-left transition"
            :class="nativeSourceClass"
            :disabled="!nativeIntegrationEnabled"
            :title="
              nativeIntegrationEnabled
                ? $t('BILLING_AUTOMATION.USE_NATIVE_SGP')
                : $t('BILLING_AUTOMATION.ENABLE_NATIVE_SGP')
            "
            @click="nativeIntegrationEnabled && (form.source_type = 'native')"
          >
            <span class="i-lucide-plug size-5 text-n-brand" /><span
              ><strong class="block text-sm text-n-slate-12">{{
                $t('BILLING_AUTOMATION.NATIVE_INTEGRATION')
              }}</strong
              ><small class="text-xs text-n-slate-11">{{
                nativeSourceDescription
              }}</small></span
            >
          </button>
        </div>
        <div v-if="form.source_type === 'native'" class="mt-3">
          <label class="block text-sm font-medium text-n-slate-12"
            >{{ $t('BILLING_AUTOMATION.PROVIDER')
            }}<select
              v-model="form.integration_provider"
              class="billing-select mt-2 w-full rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
            >
              <option value="sgp">SGP</option>
            </select></label
          >
        </div>
        <div
          v-if="form.source_type === 'webhook'"
          class="mt-4 rounded-xl border border-n-slate-5 bg-n-slate-2 p-4"
        >
          <div class="mb-4 flex items-center gap-3">
            <span
              class="flex size-9 items-center justify-center rounded-lg bg-n-brand/15 text-n-brand"
            >
              <span class="i-lucide-webhook size-5" />
            </span>
            <div>
              <h4 class="font-medium text-n-slate-12">
                {{ $t('BILLING_AUTOMATION.WEBHOOK_CONFIGURATION') }}
              </h4>
              <p class="text-xs text-n-slate-11">
                {{ $t('BILLING_AUTOMATION.WEBHOOK_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-3 md:grid-cols-[180px_1fr]">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ $t('BILLING_AUTOMATION.METHOD') }}
              <div class="relative mt-2">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2.5 text-left outline-none focus:border-n-brand"
                  @click="methodMenuOpen = !methodMenuOpen"
                >
                  <span>{{ form.webhook_method }}</span>
                  <i
                    class="i-lucide-chevron-down text-base text-n-slate-10"
                    aria-hidden="true"
                  />
                </button>
                <div
                  v-if="methodMenuOpen"
                  class="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-n-slate-5 bg-n-slate-1 shadow-xl"
                >
                  <button
                    v-for="method in ['POST', 'GET']"
                    :key="method"
                    type="button"
                    class="block w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-slate-2"
                    @click="selectMethod(method)"
                  >
                    {{ method }}
                  </button>
                </div>
              </div>
            </label>
            <label class="block text-sm font-medium text-n-slate-12">
              {{ $t('BILLING_AUTOMATION.WEBHOOK_URL') }}
              <input
                v-model="form.webhook_url"
                class="mt-2 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2.5 outline-none focus:border-n-brand"
                :placeholder="$t('BILLING_AUTOMATION.WEBHOOK_URL_PLACEHOLDER')"
              />
            </label>
          </div>
          <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div class="rounded-lg border border-n-slate-5 bg-n-slate-1 p-3">
              <div class="mb-2 flex items-center justify-between">
                <h5 class="text-sm font-medium text-n-slate-12">
                  {{ $t('BILLING_AUTOMATION.SENT_PARAMETERS') }}
                </h5>
                <span class="text-xs text-n-slate-11">{{
                  $t('BILLING_AUTOMATION.AUTOMATIC')
                }}</span>
              </div>
              <div class="space-y-2">
                <div
                  class="flex items-center gap-2 rounded-md bg-n-slate-2 px-3 py-2"
                >
                  <code
                    class="rounded bg-n-slate-4 px-2 py-0.5 text-xs text-n-brand"
                    >phone</code
                  >
                  <span class="text-xs text-n-slate-11">{{
                    $t('BILLING_AUTOMATION.CUSTOMER_PHONE')
                  }}</span>
                </div>
                <div
                  class="flex items-center gap-2 rounded-md bg-n-slate-2 px-3 py-2"
                >
                  <code
                    class="rounded bg-n-slate-4 px-2 py-0.5 text-xs text-n-brand"
                    >document</code
                  >
                  <span class="text-xs text-n-slate-11">{{
                    $t('BILLING_AUTOMATION.CUSTOMER_DOCUMENT')
                  }}</span>
                </div>
              </div>
              <p class="mt-2 text-xs text-n-slate-11">
                {{ $t('BILLING_AUTOMATION.PARAMETERS_DESCRIPTION') }}
              </p>
              <pre
                class="mt-3 overflow-x-auto rounded-md bg-n-slate-3 p-3 text-[11px] text-n-slate-11"
                >{{ '{"phone":"5511999999999","document":"12810531633"}' }}</pre
              >
            </div>
            <div class="rounded-lg border border-n-slate-5 bg-n-slate-1 p-3">
              <div class="mb-2 flex items-center justify-between">
                <h5 class="text-sm font-medium text-n-slate-12">
                  {{ $t('BILLING_AUTOMATION.HEADERS') }}
                </h5>
                <button
                  type="button"
                  class="text-xs font-medium text-n-brand hover:underline"
                  @click="addHeader"
                >
                  + {{ $t('BILLING_AUTOMATION.ADD_HEADER') }}
                </button>
              </div>
              <p class="mb-3 text-xs text-n-slate-11">
                {{ $t('BILLING_AUTOMATION.HEADERS_DESCRIPTION') }}
              </p>
              <div v-if="webhookHeaders.length" class="space-y-2">
                <div
                  v-for="(header, index) in webhookHeaders"
                  :key="index"
                  class="flex items-start gap-2"
                >
                  <input
                    v-model="header.key"
                    class="w-1/3 rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2 text-sm outline-none focus:border-n-brand"
                    :placeholder="
                      $t('BILLING_AUTOMATION.HEADER_KEY_PLACEHOLDER')
                    "
                  />
                  <input
                    v-model="header.value"
                    class="min-w-0 flex-1 rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2 text-sm outline-none focus:border-n-brand"
                    :placeholder="
                      $t('BILLING_AUTOMATION.HEADER_VALUE_PLACEHOLDER')
                    "
                  />
                  <button
                    type="button"
                    class="rounded-md p-2 text-red-500 transition hover:bg-red-500/10"
                    :title="$t('BILLING_AUTOMATION.REMOVE_HEADER')"
                    @click="removeHeader(index)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </div>
              <p v-else class="py-4 text-center text-xs italic text-n-slate-10">
                {{ $t('BILLING_AUTOMATION.NO_HEADERS') }}
              </p>
            </div>
          </div>
          <details
            class="mt-4 rounded-lg border border-n-slate-5 bg-n-slate-1 p-3"
          >
            <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
              {{ $t('BILLING_AUTOMATION.ADDITIONAL_BODY') }}
            </summary>
            <textarea
              v-model="form.webhook_body_json"
              class="mt-3 min-h-24 w-full rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2 font-mono text-xs outline-none focus:border-n-brand"
              :placeholder="bodyPlaceholder"
            />
          </details>
          <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.TEST_DESCRIPTION') }}
            </p>
            <button
              type="button"
              class="rounded-lg border border-n-brand px-4 py-2 text-xs font-medium text-n-brand transition hover:bg-n-brand/10 disabled:opacity-50"
              :disabled="testing"
              @click="testRequest"
            >
              {{
                testing
                  ? $t('BILLING_AUTOMATION.TESTING')
                  : $t('BILLING_AUTOMATION.TEST_REQUEST')
              }}
            </button>
          </div>
          <pre
            v-if="testResult"
            class="mt-3 max-h-48 overflow-auto rounded-lg bg-n-slate-3 p-3 text-xs text-n-slate-11"
            >{{ testResult }}</pre
          >
        </div>
      </div>

      <div class="mb-6 rounded-xl border border-n-slate-5 bg-n-slate-1 p-4">
        <div class="mb-4 flex items-center gap-3">
          <span
            class="flex size-8 items-center justify-center rounded-lg bg-n-brand/15 text-sm font-semibold text-n-brand"
            >02</span
          >
          <div>
            <h3 class="font-medium text-n-slate-12">
              {{ $t('BILLING_AUTOMATION.SEND_TIMING') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.SEND_TIMING_DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label class="block text-sm font-medium text-n-slate-12"
            >{{ $t('BILLING_AUTOMATION.EVENT') }}
            <div class="relative mt-2">
              <button
                type="button"
                class="flex w-full items-center justify-between rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5 text-left"
                @click="eventMenuOpen = !eventMenuOpen"
              >
                <span>{{ eventLabel }}</span>
                <i
                  class="i-lucide-chevron-down text-base text-n-slate-10"
                  aria-hidden="true"
                />
              </button>
              <div
                v-if="eventMenuOpen"
                class="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-n-slate-5 bg-n-slate-1 shadow-xl"
              >
                <button
                  v-for="event in [
                    {
                      value: 'before_due',
                      label: $t('BILLING_AUTOMATION.BEFORE_DUE'),
                    },
                    { value: 'on_due', label: $t('BILLING_AUTOMATION.ON_DUE') },
                    {
                      value: 'after_due',
                      label: $t('BILLING_AUTOMATION.AFTER_DUE'),
                    },
                  ]"
                  :key="event.value"
                  type="button"
                  class="block w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-slate-2"
                  @click="selectEvent(event.value)"
                >
                  {{ event.label }}
                </button>
              </div>
            </div></label
          >
          <label
            v-if="form.event_type !== 'on_due'"
            class="block text-sm font-medium text-n-slate-12"
          >
            {{ $t('BILLING_AUTOMATION.REFERENCE_DAYS') }}
            <div class="mt-2">
              <input
                v-model.number="form.days"
                type="number"
                min="0"
                max="30"
                class="billing-number w-full rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
                @input="clampDays"
              />
            </div>
          </label>
          <div v-else class="block text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_AUTOMATION.REFERENCE_DAYS') }}
            <div
              class="mt-2 flex min-h-[42px] items-center rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5 text-sm font-normal text-n-slate-11"
            >
              {{ $t('BILLING_AUTOMATION.SEND_ON_DUE_DATE') }}
            </div>
          </div>
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_AUTOMATION.EXECUTION_TIME') }}
            <div class="relative mt-2">
              <select
                v-model.number="form.execution_hour"
                class="w-full appearance-none rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5 pr-9 text-n-slate-12 focus:outline-none"
              >
                <option v-for="hour in 24" :key="hour - 1" :value="hour - 1">
                  {{ String(hour - 1).padStart(2, '0') }}:00
                </option>
              </select>
              <i
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 i-lucide-chevron-down text-base text-n-slate-10"
                aria-hidden="true"
              />
            </div>
            <span class="mt-1 block text-xs font-normal text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.EXECUTION_TIME_DESCRIPTION') }}
            </span>
          </label>
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_AUTOMATION.RATE_LIMIT') }}
            <input
              v-model.number="form.max_deliveries_per_minute"
              type="number"
              min="1"
              max="600"
              class="billing-number mt-2 w-full rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
            />
            <span class="mt-1 block text-xs font-normal text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.RATE_LIMIT_DESCRIPTION') }}
            </span>
          </label>
        </div>
      </div>

      <div class="mb-6 rounded-xl border border-n-slate-5 bg-n-slate-1 p-4">
        <div class="mb-4 flex items-center gap-3">
          <span
            class="flex size-8 items-center justify-center rounded-lg bg-n-brand/15 text-sm font-semibold text-n-brand"
            >03</span
          >
          <div>
            <h3 class="font-medium text-n-slate-12">
              {{ $t('BILLING_AUTOMATION.TEMPLATE_DELIVERY') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.TEMPLATE_DELIVERY_DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-5">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_AUTOMATION.APPROVED_TEMPLATE') }}
            <div class="relative mt-2">
              <button
                type="button"
                :disabled="!form.inbox_id || !approvedTemplates.length"
                class="flex w-full items-center justify-between rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-60"
                @click="templateMenuOpen = !templateMenuOpen"
              >
                <span>{{
                  form.template_name || $t('BILLING_AUTOMATION.SELECT_TEMPLATE')
                }}</span>
                <i
                  class="i-lucide-chevron-down ml-3 shrink-0 text-base text-n-slate-10"
                  aria-hidden="true"
                />
              </button>
              <div
                v-if="templateMenuOpen"
                class="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-n-slate-5 bg-n-slate-1 shadow-xl"
              >
                <button
                  v-for="template in approvedTemplates"
                  :key="template.name"
                  type="button"
                  class="block w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-slate-2"
                  @click="selectTemplate(template.name)"
                >
                  {{ template.name }}
                </button>
              </div>
            </div>
            <span
              v-if="!form.inbox_id"
              class="mt-1 block text-xs text-n-slate-11"
              >{{ $t('BILLING_AUTOMATION.SELECT_INBOX_FIRST') }}</span
            >
            <span
              v-else-if="!approvedTemplates.length"
              class="mt-1 block text-xs text-amber-500"
              >{{ $t('BILLING_AUTOMATION.NO_APPROVED_TEMPLATES') }}</span
            >
            <div
              v-if="selectedTemplate && templateVariableNames.length"
              class="mt-4 rounded-lg border border-n-slate-5 bg-n-slate-2 p-3"
            >
              <p class="text-xs font-medium text-n-slate-12">
                {{ $t('BILLING_AUTOMATION.PREVIEW_VARIABLES') }}
              </p>
              <div class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label
                  v-for="variable in templateVariableNames"
                  :key="variable"
                  class="text-xs text-n-slate-11"
                >
                  {{ $t('BILLING_AUTOMATION.VARIABLE_LABEL', { variable }) }}
                  <input
                    v-model="templateVariableValues[variable]"
                    class="mt-1 w-full rounded-md border border-n-slate-5 bg-n-slate-1 px-2.5 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                    :placeholder="
                      $t('BILLING_AUTOMATION.VARIABLE_VALUE', { variable })
                    "
                  />
                </label>
              </div>
              <p class="mt-2 text-[11px] text-n-slate-11">
                {{ $t('BILLING_AUTOMATION.PREVIEW_VARIABLES_NOTE') }}
              </p>
            </div>
          </label>
          <div class="rounded-xl border border-n-slate-5 bg-n-slate-2 p-4">
            <p class="mb-2 text-xs font-medium text-n-slate-11">
              {{ $t('BILLING_AUTOMATION.TEMPLATE_PREVIEW') }}
            </p>
            <div
              class="grid grid-cols-1 items-start gap-4 lg:grid-cols-[280px_1fr]"
            >
              <div
                class="mx-auto w-[280px] overflow-hidden rounded-[22px] border-[5px] border-n-slate-6 bg-[#efe9df] shadow-lg"
              >
                <div
                  class="flex items-center gap-2 bg-[#087e70] px-3 py-2 text-white"
                >
                  <span class="i-lucide-chevron-left size-4" />
                  <span
                    class="flex size-6 items-center justify-center rounded-full bg-white/20"
                  >
                    <span class="i-lucide-briefcase-business size-3" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-[10px] font-semibold">
                      {{ selectedTemplateHeader }}
                    </p>
                    <p class="text-[8px] opacity-80">
                      {{ $t('BILLING_AUTOMATION.BUSINESS_ACCOUNT') }}
                    </p>
                  </div>
                  <span class="i-lucide-phone size-3" />
                </div>
                <div class="min-h-[175px] bg-[#efe9df] p-2">
                  <div
                    v-if="selectedTemplate"
                    class="rounded-lg rounded-tl-none bg-[#d9ffc9] p-2 text-[10px] leading-4 text-slate-800 shadow-sm"
                  >
                    <div
                      v-if="selectedTemplatePayment"
                      class="mb-2 rounded border border-slate-300 bg-white p-2 text-slate-700"
                    >
                      <p
                        class="text-[9px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        {{ $t('BILLING_AUTOMATION.INVOICE_PAYMENT') }}
                      </p>
                      <div
                        class="mt-1 flex items-center justify-between gap-2 border-t border-slate-200 pt-1"
                      >
                        <span class="text-[9px]">{{
                          $t('BILLING_AUTOMATION.PAY_WITH_PIX')
                        }}</span>
                        <span class="text-[10px] font-semibold">{{
                          templateVariableValues.valor
                        }}</span>
                      </div>
                    </div>
                    <img
                      v-if="selectedTemplateImage"
                      :src="selectedTemplateImage"
                      :alt="$t('BILLING_AUTOMATION.TEMPLATE_IMAGE_ALT')"
                      class="mb-2 h-20 w-full rounded object-cover"
                    />
                    <p class="whitespace-pre-line">
                      {{ selectedTemplatePreview }}
                    </p>
                    <div
                      v-if="selectedTemplateButtons.length"
                      class="mt-2 -mx-2 border-t border-slate-200 bg-white text-center text-[10px] text-sky-600"
                    >
                      <div
                        v-for="button in selectedTemplateButtons"
                        :key="button.label || button.text"
                        class="border-b border-slate-200 px-2 py-1.5 last:border-b-0"
                      >
                        {{ button.label || button.text }}
                      </div>
                    </div>
                    <p class="mt-1 text-right text-[8px] text-slate-500">
                      {{ $t('BILLING_AUTOMATION.NOW') }} ✓✓
                    </p>
                  </div>
                  <p
                    v-else
                    class="pt-16 text-center text-[10px] text-slate-500"
                  >
                    {{ $t('BILLING_AUTOMATION.SELECT_TEMPLATE_PREVIEW') }}
                  </p>
                </div>
              </div>
              <div class="rounded-lg border border-n-slate-5 bg-n-slate-1 p-3">
                <div class="mb-2 flex items-center justify-between gap-2">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ $t('BILLING_AUTOMATION.AVAILABLE_VARIABLES') }}
                  </p>
                  <span class="text-[11px] text-n-slate-11">{{
                    $t('BILLING_AUTOMATION.CLICK_TO_COPY')
                  }}</span>
                </div>
                <div v-if="availableVariables.length" class="space-y-2">
                  <button
                    v-for="variable in availableVariables"
                    :key="`${variable.token}-${variable.source}`"
                    type="button"
                    class="flex w-full items-center justify-between gap-3 rounded-md bg-n-slate-2 px-3 py-2 text-left transition hover:bg-n-brand/10"
                    @click="navigator.clipboard?.writeText(variable.token)"
                  >
                    <code class="text-xs text-n-brand">{{
                      variable.token
                    }}</code>
                    <span class="text-[11px] text-n-slate-11">{{
                      variable.source
                    }}</span>
                  </button>
                </div>
                <p
                  v-else
                  class="rounded-md bg-n-slate-2 px-3 py-4 text-center text-xs text-n-slate-11"
                >
                  {{ $t('BILLING_AUTOMATION.TEST_WEBHOOK_TO_LOAD_FIELDS') }}
                </p>
                <p class="mt-3 text-[11px] text-n-slate-11">
                  {{ $t('BILLING_AUTOMATION.VARIABLES_DESCRIPTION') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <details
        v-if="form.source_type === 'webhook'"
        class="mb-5 rounded-xl border border-n-slate-5 bg-n-slate-1 p-4"
      >
        <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
          {{ $t('BILLING_AUTOMATION.ADVANCED_RESPONSE') }}
        </summary>
        <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <input
            v-model="form.response_mapping.invoices_path"
            class="rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
            :placeholder="$t('BILLING_AUTOMATION.INVOICES_PATH_PLACEHOLDER')"
          />
          <input
            v-model="form.response_mapping.due_date_path"
            class="rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
            :placeholder="$t('BILLING_AUTOMATION.DUE_DATE_PATH_PLACEHOLDER')"
          />
          <input
            v-model="form.response_mapping.amount_path"
            class="rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
            :placeholder="$t('BILLING_AUTOMATION.AMOUNT_PATH_PLACEHOLDER')"
          />
          <input
            v-model="form.response_mapping.link_path"
            class="rounded-lg border border-n-slate-5 bg-n-slate-2 px-3 py-2.5"
            :placeholder="$t('BILLING_AUTOMATION.LINK_PATH_PLACEHOLDER')"
          />
        </div>
      </details>

      <div
        class="flex items-center justify-between gap-4 border-t border-n-slate-5 pt-5"
      >
        <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
        <span v-else class="text-xs text-n-slate-11">
          {{ $t('BILLING_AUTOMATION.RULE_STARTS_DISABLED') }}
        </span>
        <button
          class="rounded-lg bg-n-brand px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:brightness-110 disabled:opacity-50"
          :disabled="saving"
          @click="save"
        >
          {{
            saving
              ? $t('BILLING_AUTOMATION.SAVING')
              : editingRuleId
                ? $t('BILLING_AUTOMATION.UPDATE_RULE')
                : $t('BILLING_AUTOMATION.CREATE_RULE_SHORT')
          }}
        </button>
        <button
          v-if="editingRuleId"
          type="button"
          class="rounded-lg border border-n-slate-5 px-5 py-2.5 text-sm text-n-slate-11 transition hover:border-n-brand hover:text-n-brand"
          @click="cancelEdit"
        >
          {{ $t('BILLING_AUTOMATION.CANCEL_EDIT') }}
        </button>
      </div>
    </section>

    <section>
      <div class="mb-3 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('BILLING_AUTOMATION.YOUR_RULES') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('BILLING_AUTOMATION.YOUR_RULES_DESCRIPTION') }}
          </p>
        </div>
        <span
          class="rounded-full bg-n-slate-2 px-3 py-1 text-xs text-n-slate-11"
          >{{
            $t('BILLING_AUTOMATION.RULES_COUNT', { count: rules.length })
          }}</span
        >
      </div>
      <div class="flex flex-col gap-3">
        <div
          v-for="rule in rules"
          :key="rule.id"
          class="flex flex-col gap-4 rounded-xl border border-n-slate-5 bg-n-slate-2 p-4 shadow-sm transition hover:border-n-slate-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-1 flex size-9 items-center justify-center rounded-lg"
              :class="
                rule.active
                  ? 'bg-green-500/15 text-green-500'
                  : 'bg-n-slate-1 text-n-slate-10'
              "
              ><span :class="rule.active ? 'i-lucide-zap' : 'i-lucide-pause'"
            /></span>
            <div>
              <div class="flex items-center gap-2 font-medium text-n-slate-12">
                {{ rule.name
                }}<span
                  class="rounded-full px-2 py-0.5 text-[10px] font-medium"
                  :class="
                    rule.active
                      ? 'bg-green-500/15 text-green-500'
                      : 'bg-n-slate-1 text-n-slate-10'
                  "
                  >{{
                    rule.active
                      ? $t('BILLING_AUTOMATION.ACTIVE')
                      : $t('BILLING_AUTOMATION.DRAFT')
                  }}</span
                >
              </div>
              <div class="mt-1 text-sm text-n-slate-11">
                {{ eventLabelFor(rule.event_type) }} · {{ rule.days }}
                {{ $t('BILLING_AUTOMATION.DAYS') }} ·
                {{ String(rule.execution_hour ?? 2).padStart(2, '0') }}:00 ·
                {{
                  rule.source_type === 'native'
                    ? 'SGP'
                    : $t('BILLING_AUTOMATION.WEBHOOK')
                }}
              </div>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              class="rounded-lg border border-n-slate-5 px-3 py-2 text-xs text-n-slate-11 transition hover:border-n-brand hover:text-n-brand"
              @click="editRule(rule)"
            >
              {{ $t('BILLING_AUTOMATION.EDIT') }}
            </button>
            <button
              class="rounded-lg border border-n-slate-5 px-3 py-2 text-xs text-n-slate-11 transition hover:border-n-brand hover:text-n-brand"
              @click="toggle(rule)"
            >
              {{
                rule.active
                  ? $t('BILLING_AUTOMATION.DEACTIVATE')
                  : $t('BILLING_AUTOMATION.ACTIVATE')
              }}</button
            ><button
              class="rounded-lg border border-red-500/30 px-3 py-2 text-xs text-red-500 transition hover:bg-red-500/10"
              @click="remove(rule)"
            >
              {{ $t('BILLING_AUTOMATION.REMOVE') }}
            </button>
          </div>
        </div>
        <div
          v-if="!rules.length"
          class="rounded-xl border border-dashed border-n-slate-5 bg-n-slate-2 p-10 text-center"
        >
          <span
            class="i-lucide-receipt-text mx-auto mb-3 block size-8 text-n-slate-9"
          />
          <div class="font-medium text-n-slate-12">
            {{ $t('BILLING_AUTOMATION.EMPTY_TITLE') }}
          </div>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('BILLING_AUTOMATION.EMPTY_DESCRIPTION') }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
select.billing-select {
  appearance: none !important;
  -webkit-appearance: none !important;
}

.billing-number {
  appearance: textfield;
}

.billing-number::-webkit-inner-spin-button,
.billing-number::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}
</style>
